import { Link, useParams } from "wouter";
import { GuideLinks } from "@/components/GuideLinks";
import { getCategoryById, visibleCategories } from "@/lib/tools-data";
import { getCategoryName, getCategoryDescription } from "@/lib/i18n/category-persona-translations";
import { getCategoryContent } from "@/lib/category-content";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FavoriteButton } from "@/components/FavoriteButton";
import { InstallToolButton } from "@/components/InstallToolButton";
import { usePageMeta } from "@/hooks/usePageMeta";
import { usePwaManifest } from "@/hooks/usePwaManifest";
import { useStructuredData } from "@/hooks/useStructuredData";
import { useLocale } from "@/hooks/useLocale";
import { UsageAndUpgradeStrip } from "@/components/UsageAndUpgradeStrip";
import { FEATURED_APP_IDS } from "@/lib/featured-apps-data";

export default function CategoryPage() {
  const { id } = useParams<{ id: string }>();
  const { locale, localizedHref } = useLocale();
  const rawCategory = getCategoryById(id || "");
  // Treat hidden categories (currently just AI Studio) the same as
  // not-found on a direct visit -- they aren't ready for real traffic yet.
  const category = rawCategory && !rawCategory.hidden ? rawCategory : undefined;
  // Swaps in this category's own scoped manifest (scope: "/", so
  // installing it creates a personalized home screen someone can still
  // navigate the whole site from -- see generate-category-manifests.mjs
  // for the full reasoning) while this page is mounted.
  usePwaManifest(category ? `category-${category.id}` : null);
  // Individual tools can also be hidden from discovery within an otherwise
  // visible category (a network-dependent AI tool not meeting the site's
  // "just works" bar, for example) -- this is what the grid and tool count
  // should actually reflect, not the raw unfiltered list.
  const visibleTools = category ? category.tools.filter(t => !t.hidden && !FEATURED_APP_IDS.has(t.id)) : [];
  const categoryName = category ? getCategoryName(locale, category.id, category.name) : "";
  const categoryDescription = category ? getCategoryDescription(locale, category.id, category.description) : "";
  const categoryContent = category ? getCategoryContent(category.id) : undefined;

  usePageMeta({
    title: category ? categoryName : "Category Not Found",
    description: category
      ? `${categoryDescription} ${visibleTools.length} free, professional-grade tools, no signup required for most.`
      : undefined,
    path: `/category/${id ?? ""}`,
    noindex: !category,
  });

  useStructuredData(
    category
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.freedailypro.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: categoryName || category.name,
              item: `https://www.freedailypro.com/category/${category.id}`,
            },
          ],
        }
      : null
  );

  if (!category) {
    return (
      <div className="container py-20 text-center">
        <h1 className="text-2xl font-bold mb-4">Category Not Found</h1>
        <Link href="/">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="page-enter">
      {/* Header */}
      <section className="border-b border-border/50 bg-muted/20">
        <div className="container py-10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link href="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link href={localizedHref("/tools")} className="hover:text-foreground transition-colors">
              All Categories
            </Link>
            <span>/</span>
            <span className="text-foreground font-medium">{categoryName}</span>
          </div>
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${category.color}15` }}
            >
              <category.icon className="h-6 w-6" style={{ color: category.color }} />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight gradient-text">{categoryName}</h1>
              <p className="text-muted-foreground mt-1">{categoryDescription}</p>
            </div>
            <InstallToolButton toolName={categoryName} className="h-9 w-9 flex items-center justify-center rounded-lg border border-border/60 hover:border-primary/40" />
          </div>
        </div>
      </section>

      {categoryContent && (
        <section className="container pt-8">
          <p className="text-sm text-foreground/90 leading-relaxed max-w-3xl">{categoryContent}</p>
        </section>
      )}

      <section className="container pt-8">
        <UsageAndUpgradeStrip />
      </section>

      {/* Tools Grid */}
      <section className="container py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleTools.map((tool) => (
            <Link key={tool.id} href={localizedHref(`/tool/${tool.id}`)}>
              <div className="tool-card group p-5 rounded-xl border border-border/60 bg-card hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 cursor-pointer h-full flex flex-col transition-all duration-200">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <h2 className="font-semibold text-base group-hover:text-primary transition-colors">{tool.name}</h2>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {tool.isAI && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">
                        <Sparkles className="mr-0.5 h-2.5 w-2.5" /> AI
                      </Badge>
                    )}
                    <FavoriteButton toolId={tool.id} />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                  {tool.description}
                </p>
                <div className="mt-3 flex items-center text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:translate-x-0.5">
                  Open Tool <ArrowRight className="ml-1 h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <GuideLinks kind="topics" id={category.id} heading={`${categoryName} guides`} />

      {/* Other Categories */}
      <section className="container py-10 border-t border-border/50">
        <h2 className="text-lg font-semibold mb-4 card-heading">Explore Other Categories</h2>
        <div className="flex flex-wrap gap-2">
          {visibleCategories
            .filter((c) => c.id !== category.id)
            .map((c) => (
              <Link key={c.id} href={localizedHref(`/category/${c.id}`)}>
                <Badge variant="outline" className="px-3 py-1.5 cursor-pointer hover:bg-accent transition-colors">
                  <c.icon className="mr-1.5 h-3 w-3" style={{ color: c.color }} />
                  {getCategoryName(locale, c.id, c.name)}
                </Badge>
              </Link>
            ))}
        </div>
      </section>
    </div>
  );
}
